/* SMUKIEZ — sound, click-to-leave, grain, sound toggle.
 *
 * Every link click plays the click sound, flashes the link red, fades the page and only
 * THEN navigates (settings.smk_click_delay ms, default 850), so the sound is never cut
 * off by the next page loading. Sounds decode through Web Audio so they can overlap and
 * sit at their own volume; HTMLAudio is the fallback. Settings arrive as window.SMK. */
(() => {
  const S = Object.assign(
    { clickDelay: 850, hoverSound: false, grain: true, sounds: {}, volumes: {} },
    window.SMK || {}
  );
  const reduced = matchMedia("(prefers-reduced-motion: reduce)").matches;
  const KEY_SOUND = "smukiez.sound";

  /* ---------------------------------------------------------------- sound */
  const Sound = (() => {
    let ctx = null;
    const buffers = {}, pending = {};
    let enabled = true;
    try { enabled = localStorage.getItem(KEY_SOUND) !== "0"; } catch (e) { /* no storage */ }

    function context() {
      if (ctx) return ctx;
      const C = window.AudioContext || window.webkitAudioContext;
      if (!C) return null;
      try { ctx = new C(); } catch (e) { ctx = null; }
      return ctx;
    }
    function load(name) {
      const url = S.sounds[name];
      if (!url || buffers[name]) return Promise.resolve(buffers[name] || null);
      if (!pending[name]) {
        pending[name] = fetch(url, { mode: "cors" })
          .then(r => (r.ok ? r.arrayBuffer() : null))
          .then(b => (b && context() ? context().decodeAudioData(b) : null))
          .then(buf => (buffers[name] = buf))
          .catch(() => null);
      }
      return pending[name];
    }
    function unlock() {
      const c = context();
      if (c && c.state === "suspended") c.resume().catch(() => {});
      Object.keys(S.sounds).forEach(load);
    }
    function play(name, opts) {
      if (!enabled || !S.sounds[name]) return;
      const o = Object.assign({ volume: S.volumes[name] != null ? S.volumes[name] : 1, rate: 1 }, opts || {});
      const c = context();
      if (c && buffers[name] && c.state === "running") {
        try {
          const src = c.createBufferSource();
          src.buffer = buffers[name];
          src.playbackRate.value = o.rate;
          const g = c.createGain();
          g.gain.value = o.volume;
          src.connect(g).connect(c.destination);
          src.start();
          return;
        } catch (e) { /* fall through */ }
      }
      load(name);
      try {
        const a = new Audio(S.sounds[name]);
        a.volume = Math.max(0, Math.min(1, o.volume));
        a.playbackRate = o.rate;
        a.play().catch(() => {});
      } catch (e) { /* blocked until a gesture */ }
    }
    function set(on) {
      enabled = !!on;
      try { localStorage.setItem(KEY_SOUND, enabled ? "1" : "0"); } catch (e) { /* ignore */ }
      document.dispatchEvent(new CustomEvent("smk:sound", { detail: { enabled } }));
    }
    return { play, unlock, set, get enabled() { return enabled; } };
  })();

  const unlockOnce = () => { Sound.unlock(); ["pointerdown", "keydown", "touchstart"].forEach(t => document.removeEventListener(t, unlockOnce, true)); };
  ["pointerdown", "keydown", "touchstart"].forEach(t => document.addEventListener(t, unlockOnce, true));

  /* ---------------------------------------------------------------- leaving */
  let leaving = false;
  const wipe = document.createElement("div");
  wipe.className = "smk-wipe";
  document.documentElement.style.setProperty("--smk-leave", S.clickDelay + "ms");

  function leave(url, opts) {
    if (leaving) return;
    leaving = true;
    const delay = reduced ? 0 : (opts && opts.delay != null ? opts.delay : S.clickDelay);
    document.body.appendChild(wipe);
    requestAnimationFrame(() => document.documentElement.classList.add("smk-leaving"));
    setTimeout(() => { window.location.href = url; }, delay);
  }
  // Back/forward cache can bring a faded page back: undo it.
  window.addEventListener("pageshow", () => { leaving = false; document.documentElement.classList.remove("smk-leaving"); if (wipe.parentNode) wipe.remove(); });

  const sameWindowNav = a => {
    const href = a.getAttribute("href") || "";
    if (!href || href.startsWith("#") || /^(javascript|mailto|tel|sms):/i.test(href)) return false;
    if (a.target && a.target !== "_self") return false;
    if (a.hasAttribute("download") || a.hasAttribute("data-smk-no-delay")) return false;
    return true;
  };

  document.addEventListener("click", e => {
    if (e.defaultPrevented) return;
    const a = e.target.closest("a[href]");
    if (a) {
      if (!sameWindowNav(a)) { Sound.play("click"); return; }
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
      e.preventDefault();
      Sound.play("click");
      a.classList.add("is-pressed");
      leave(a.href);
      return;
    }
    const b = e.target.closest("button, [role='button'], input[type='submit'], summary, label[for]");
    if (b && !b.hasAttribute("data-smk-silent")) Sound.play("click", { volume: (S.volumes.click != null ? S.volumes.click : 1) * 0.8 });
  }, true);

  // Forms that go to a new page (search, contact, login, checkout) get the same beat.
  document.addEventListener("submit", e => {
    const f = e.target;
    if (!(f instanceof HTMLFormElement) || e.defaultPrevented) return;
    const action = (f.getAttribute("action") || "").toLowerCase();
    if (!action || /\/cart\/add|\/cart\/update|\/cart\/change/.test(action) || f.hasAttribute("data-smk-no-delay")) return;
    if (f.target && f.target !== "_self") return;
    if (leaving) { e.preventDefault(); return; }
    e.preventDefault();
    Sound.play("click");
    leaving = true;
    document.body.appendChild(wipe);
    requestAnimationFrame(() => document.documentElement.classList.add("smk-leaving"));
    setTimeout(() => { leaving = false; f.submit(); }, reduced ? 0 : S.clickDelay);
  }, true);

  /* ---------------------------------------------------------------- hover tick */
  if (S.hoverSound && matchMedia("(hover: hover)").matches) {
    let last = 0;
    document.addEventListener("pointerover", e => {
      const t = e.target.closest("a[href], button, .smk-btn, label[for]");
      if (!t || t.contains(e.relatedTarget)) return;
      const now = performance.now();
      if (now - last < 70) return;
      last = now;
      Sound.play("hover", { volume: 0.25, rate: 0.96 + Math.random() * 0.08 });
    }, true);
  }

  /* ---------------------------------------------------------------- chrome */
  function mountChrome() {
    if (S.grain && !document.querySelector(".smk-grain")) {
      const g = document.createElement("div");
      g.className = "smk-grain";
      g.setAttribute("aria-hidden", "true");
      document.body.appendChild(g);
    }
    if (Object.keys(S.sounds).length && !document.querySelector(".smk-sound")) {
      const t = document.createElement("button");
      t.type = "button";
      t.className = "smk-sound";
      t.setAttribute("data-smk-silent", "");
      const render = () => {
        t.setAttribute("aria-pressed", Sound.enabled ? "true" : "false");
        t.innerHTML = `<span class="smk-sound__bars"><i></i><i></i><i></i><i></i></span><span>${Sound.enabled ? "Sound on" : "Sound off"}</span>`;
      };
      render();
      t.addEventListener("click", () => { Sound.set(!Sound.enabled); Sound.unlock(); if (Sound.enabled) Sound.play("click"); render(); });
      document.body.appendChild(t);
    }
  }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", mountChrome); else mountChrome();

  /* ---------------------------------------------------------------- reveal */
  // Elements tagged data-smk-reveal fade/slide in as they enter the viewport.
  const io = "IntersectionObserver" in window && !reduced
    ? new IntersectionObserver(es => es.forEach(x => { if (x.isIntersecting) { x.target.classList.add("is-in"); io.unobserve(x.target); } }), { rootMargin: "0px 0px -8% 0px" })
    : null;
  const arm = root => (root || document).querySelectorAll("[data-smk-reveal]:not(.is-in)").forEach(el => (io ? io.observe(el) : el.classList.add("is-in")));
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", () => arm()); else arm();
  document.addEventListener("shopify:section:load", e => arm(e.target));

  window.SMK = Object.assign(S, { Sound, leave, arm });
})();
